import type { RouterOutput } from "@lattice/backend/trpc";
import { cn } from "@lattice/frontend/lib/utils/ui";
import {
  separatorLineClass,
  sectionSeparatorLineClass,
  sectionContainerClass,
  sectionHeadingClass,
  sectionHeaderSpacing,
  sectionSeparatorSpacing,
  cardGridGap,
} from "@lattice/frontend/lib/styles/common";
import InlineSummary from "./inline-summary";
import { Suspense } from "react";
import StockStatisticsSectionContent from "./stock-statistics-section-content";
import { Skeleton } from "../ui/skeleton";

export const key_metrics_groups: {
  name: string;
  stats: {
    key: keyof NonNullable<
      RouterOutput["stock"]["get_statistics"]["key_metrics"]
    >;
    name: string;
  }[];
}[] = [
  {
    name: "Valuation Metrics",
    stats: [
      { key: "marketCap", name: "Market Cap" },
      { key: "enterpriseValueTTM", name: "Enterprise Value" },
      { key: "evToSalesTTM", name: "EV to Sales" },
      { key: "evToFreeCashFlowTTM", name: "EV to Free Cash Flow" },
      { key: "evToEBITDATTM", name: "EV to EBITDA" },
      { key: "beta", name: "Beta" },
    ],
  },
  {
    name: "Profitability & Return",
    stats: [
      { key: "returnOnAssetsTTM", name: "Return on Assets" },
      { key: "returnOnEquityTTM", name: "Return on Equity" },
      {
        key: "returnOnInvestedCapitalTTM",
        name: "Return on Invested Capital",
      },
      { key: "earningsYieldTTM", name: "Earnings Yield" },
      { key: "freeCashFlowYieldTTM", name: "Free Cash Flow Yield" },
    ],
  },
  {
    name: "Liquidity & Leverage",
    stats: [
      { key: "currentRatioTTM", name: "Current Ratio" },
      { key: "netDebtToEBITDATTM", name: "Net Debt to EBITDA" },
    ],
  },
];

export const financial_ratios_groups: {
  name: string;
  stats: {
    key: keyof NonNullable<RouterOutput["stock"]["get_statistics"]["ratios"]>;
    name: string;
  }[];
}[] = [
  {
    name: "Valuation Ratios",
    stats: [
      { key: "priceToEarningsRatioTTM", name: "P/E Ratio" },
      { key: "priceToEarningsGrowthRatioTTM", name: "PEG Ratio" },
      { key: "priceToBookRatioTTM", name: "P/B Ratio" },
      { key: "priceToSalesRatioTTM", name: "P/S Ratio" },
      { key: "priceToFreeCashFlowRatioTTM", name: "P/FCF Ratio" },
      { key: "enterpriseValueMultipleTTM", name: "EV Multiple" },
    ],
  },
  {
    name: "Margins & Efficiency",
    stats: [
      { key: "grossProfitMarginTTM", name: "Gross Profit Margin" },
      { key: "ebitdaMarginTTM", name: "EBITDA Margin" },
      { key: "operatingProfitMarginTTM", name: "Operating Profit Margin" },
      { key: "netProfitMarginTTM", name: "Net Profit Margin" },
      { key: "assetTurnoverTTM", name: "Asset Turnover" },
      { key: "interestCoverageRatioTTM", name: "Interest Coverage Ratio" },
    ],
  },
  {
    name: "Per Share Data",
    stats: [
      { key: "revenuePerShareTTM", name: "Revenue per Share" },
      { key: "netIncomePerShareTTM", name: "Earnings per Share (EPS)" },
      { key: "cashPerShareTTM", name: "Cash per Share" },
      { key: "bookValuePerShareTTM", name: "Book Value per Share" },
      { key: "freeCashFlowPerShareTTM", name: "FCF per Share" },
    ],
  },
  {
    name: "Dividend Metrics",
    stats: [{ key: "dividendYieldTTM", name: "Dividend Yield" }],
  },
];

export default function StockStatisticsSection({ symbol }: { symbol: string }) {
  return (
    <div className={cn(sectionContainerClass, "bg-background")}>
      <h2
        className={cn(
          sectionHeadingClass,
          sectionHeaderSpacing,
          "scroll-mt-32",
        )}
        id="statistics"
      >
        Stats
      </h2>
      <div className={cn(sectionSeparatorSpacing, sectionSeparatorLineClass)} />

      <InlineSummary
        section="statistics"
        isVisible={true}
        symbol={symbol}
        is_crypto={false}
      />

      <Suspense
        fallback={
          <>
            <div className={cn("grid grid-cols-1 lg:grid-cols-2", cardGridGap)}>
              <div>
                {key_metrics_groups.map((group) => (
                  <div key={group.name} className="mb-10 sm:mb-8">
                    <h4 className="font-sans text-xs font-normal text-muted-foreground/80 uppercase tracking-wider mb-2">
                      {group.name}
                    </h4>
                    <ul className="space-y-2">
                      {group.stats.map((stat) => (
                        <li
                          key={stat.key}
                          className={cn(
                            "py-1 flex justify-between border-dashed",
                            separatorLineClass,
                          )}
                        >
                          <span className="text-sm sm:text-base">
                            {stat.name}
                          </span>
                          <Skeleton className="w-16 h-4" />
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              <div>
                {financial_ratios_groups.map((group) => (
                  <div key={group.name} className="mb-10 sm:mb-8">
                    <h4 className="font-sans text-xs font-normal text-muted-foreground/80 uppercase tracking-wider mb-2">
                      {group.name}
                    </h4>
                    <ul className="space-y-2">
                      {group.stats.map((stat) => (
                        <li
                          key={stat.key}
                          className={cn(
                            "py-1 flex justify-between border-dashed",
                            separatorLineClass,
                          )}
                        >
                          <span className="text-sm sm:text-base">
                            {stat.name}
                          </span>
                          <Skeleton className="w-16 h-4" />
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </>
        }
      >
        <StockStatisticsSectionContent symbol={symbol} />
      </Suspense>
    </div>
  );
}
